import {
  CustomFieldDetail,
  EndOrderDetails,
  CartItem,
  PaymentMethod,
  FirestoreTimestamp,
  firestoreDateParser,
  OrderItemLineItem,
  AliquotAmount,
} from '@pedix-workspace/utils';
import {
  OrderHeadingParams,
  OrderItemsByCategories,
  OrderTotalParams,
  OrderItem,
} from '../order-message.types';
import { getOrderAddress, getOrderAddressHints } from '@pedix-workspace/shared-models';

import { format } from 'date-fns';
import { parseCustomFieldValue } from '../parse-custom-field-values';
import {
  getItemPrice,
  getItemType,
  getProductOrSinglePresentationQuantity,
  getProductOrSinglePresentationTotal,
  getProductOrSinglePresentationUnitPrice,
  getOptionItemTotal,
  getOptionItemUnitPrice,
  getOptionItemQuantity,
  getOptionItemMultiplier,
} from '../cart-item/cart-item-utils';

export function endOrderToOrderHeadingParams(
  endOrder: EndOrderDetails,
  siteUrl: string,
  slug: string,
): OrderHeadingParams {
  const displayMapUrl = endOrder.personalInformation.address?.addressDetails?.url;

  return {
    id: endOrder.id,
    sequenceId: endOrder.sequenceId,
    slug,
    date: firestoreDateParser(endOrder.created),
    name: endOrder.personalInformation.name,
    phone: endOrder.personalInformation.phone,
    paymentMethod: endOrder.paymentMethod?.name,
    paymentMethodType: endOrder.paymentMethod?.type,
    couponCode: endOrder.couponConfigurationSnapshot?.code,
    finalAmount: endOrder.finalAmount,
    paymentMethodHowMuch: endOrder.howMuch,
    paymentLink: hasPaymentLink(endOrder.paymentMethod)
      ? getPaymentUrl(siteUrl, endOrder.id)
      : undefined,
    paymentLegend: endOrder.paymentMethod?.legend?.trim(),
    shippingOptionType: endOrder.shippingOption?.type,
    address: getOrderAddress(endOrder.personalInformation.address, endOrder.shippingOption?.type, {
      includeHints: false,
    }),
    addressHints: getOrderAddressHints(
      endOrder.personalInformation.address,
      endOrder.shippingOption?.type,
    ),
    mapUrl: displayMapUrl ? getMapUrl(siteUrl, endOrder.id) : undefined,
    tableNumber: endOrder.tableNumber,
    customFieldDetails: getSanitizedCustomFieldDetails(endOrder.customFieldDetails),
    trackingUrl: endOrder.shippingEstimate ? getTrackingUrl(siteUrl, endOrder.id) : undefined,
    shippingProviderName: endOrder.shippingEstimate?.providerName,
    parcelTrackingEmail: endOrder.personalInformation.email,
  };
}

export function endOrderToOrderItemsByCategories(cartItems: CartItem[]): OrderItemsByCategories {
  const cartItemsByCategory = getCartItemsByCategory(cartItems);
  const categoryNames = Object.keys(cartItemsByCategory);

  return {
    categories: categoryNames.map(categoryName => {
      const itemsByGroup = cartItemsByCategory[categoryName];
      const groupNames = Object.keys(itemsByGroup);

      // Leave undefined groups at the end
      groupNames.sort(sortAsscending);

      const groups = groupNames.map(groupName => ({
        name: groupName === 'undefined' ? undefined : groupName,
        isCustomGroup: groupName !== 'undefined',
        items: itemsByGroup[groupName].map(endOrderCartItemToOrderItem),
      }));
      return {
        categoryName,
        hasCustomGroups: groupNames.some(groupName => groupName !== 'undefined'),
        groups,
      };
    }),
  };
}

export function sortedCartItemsByCategoryAndGroup(cartItems: CartItem[]): CartItem[] {
  const cartItemsByCategory = getCartItemsByCategory(cartItems);
  const categoryNames = Object.keys(cartItemsByCategory);
  const sortedCartItems: CartItem[] = [];

  categoryNames.forEach(categoryName => {
    const itemsByGroup = cartItemsByCategory[categoryName];
    const groupNames = Object.keys(itemsByGroup);

    groupNames.sort(sortAsscending);

    groupNames.forEach(groupName => {
      itemsByGroup[groupName].forEach(cartItem => {
        sortedCartItems.push(cartItem);
      });
    });
  });

  return sortedCartItems;
}

export function endOrderLineItems(
  order: Pick<
    EndOrderDetails,
    | 'cartItems'
    | 'deliveryCost'
    | 'couponDiscount'
    | 'paymentMethodExtraCharge'
    | 'paymentMethodDiscount'
  >,
  params: { defaultAliquot?: AliquotAmount },
) {
  return endOrderCartItemsToOrderLineItems(order.cartItems, {
    defaultAliquot: params.defaultAliquot,
  }).concat(
    endOrderPriceModifiersToOrderLineItems(order, {
      defaultAliquot: params.defaultAliquot,
    }),
  );
}

export function endOrderCartItemsToOrderLineItems(
  cartItems: CartItem[],
  params: { defaultAliquot?: AliquotAmount },
): OrderItemLineItem[] {
  const sortedCartItems = sortedCartItemsByCategoryAndGroup(cartItems);

  return sortedCartItems
    .map(endOrderCartItemToOrderItem)
    .map(orderItem => orderItemToOrderItemLineItems(orderItem, params))
    .flat();
}

export function endOrderPriceModifiersToOrderLineItems(
  priceModifiers: Pick<
    EndOrderDetails,
    'deliveryCost' | 'couponDiscount' | 'paymentMethodExtraCharge' | 'paymentMethodDiscount'
  >,
  params: { defaultAliquot?: AliquotAmount },
): OrderItemLineItem[] {
  const lineItems: OrderItemLineItem[] = [];

  if (priceModifiers.deliveryCost > 0) {
    lineItems.push({
      categoryName: undefined,
      groupName: undefined,
      name: 'Costo de envío',
      quantity: 1,
      unitPrice: priceModifiers.deliveryCost,
      total: priceModifiers.deliveryCost,
      aliquot: params.defaultAliquot,
      type: 'price-modifier',
    });
  }
  if (priceModifiers.paymentMethodExtraCharge > 0) {
    lineItems.push({
      categoryName: undefined,
      groupName: undefined,
      name: 'Cargos extra',
      quantity: 1,
      unitPrice: priceModifiers.paymentMethodExtraCharge,
      total: priceModifiers.paymentMethodExtraCharge,
      aliquot: params.defaultAliquot,
      type: 'price-modifier',
    });
  }
  if (priceModifiers.couponDiscount > 0 || priceModifiers.paymentMethodDiscount > 0) {
    const totalDiscount =
      ((priceModifiers.couponDiscount || 0) + (priceModifiers.paymentMethodDiscount || 0)) * -1;

    lineItems.push({
      categoryName: undefined,
      groupName: undefined,
      name: 'Descuentos',
      quantity: 1,
      unitPrice: totalDiscount,
      total: totalDiscount,
      aliquot: params.defaultAliquot,
      type: 'price-modifier',
    });
  }
  return lineItems;
}

export function endOrderCartItemToOrderItem(cartItem: CartItem): OrderItem {
  const itemType = getItemType(cartItem);

  let productOrPresentationSku;

  if (itemType === 'single-presentation') {
    productOrPresentationSku = cartItem.selectedPresentation.items[0].sku;
  } else if (itemType === 'regular') {
    productOrPresentationSku = cartItem.product.sku;
  }

  return {
    cartItem: cartItem,
    productName: cartItem.product.name,
    productOrPresentationSku,
    total: getProductOrSinglePresentationTotal(cartItem, { usePriceDiscount: true }),
    quantity: getProductOrSinglePresentationQuantity(cartItem),
    unitPrice: getProductOrSinglePresentationUnitPrice(cartItem, { usePriceDiscount: true }),
    observations: cartItem.observations,
    type: itemType,
    options: cartItem.selectedOptions?.map(({ option, items }) => ({
      name: option.name,
      items: items.map(item => {
        const productMultiplier = getOptionItemMultiplier(cartItem);

        return {
          name: item.name,
          quantity: getOptionItemQuantity(option, item, productMultiplier),
          price: getOptionItemTotal(option, item, productMultiplier, { usePriceDiscount: true }),
          unitPrice: getOptionItemUnitPrice(item, { usePriceDiscount: true }),
          aliquot: item.aliquot,
        };
      }),
    })),
    presentationName:
      itemType === 'single-presentation' ? cartItem.selectedPresentation.items[0].name : undefined,
    presentations:
      itemType === 'multi-presentation'
        ? {
            items: cartItem.selectedPresentation.items.map(item => ({
              name: item.name,
              sku: item.sku,
              quantity: item.quantity || 1,
              price: getItemPrice(item, { usePriceDiscount: true }) * (item.quantity || 1),
              unitPrice: getItemPrice(item, { usePriceDiscount: true }),
            })),
          }
        : undefined,
  };
}

export function orderItemToOrderItemLineItems(
  orderItem: OrderItem,
  params: { defaultAliquot?: AliquotAmount },
): OrderItemLineItem[] {
  const lineItems: OrderItemLineItem[] = [];

  switch (orderItem.type) {
    case 'regular':
    case 'single-presentation':
      lineItems.push({
        categoryName: orderItem.cartItem.category.name,
        groupName: orderItem.cartItem.product.groupName,
        name:
          orderItem.type === 'regular'
            ? orderItem.productName
            : `${orderItem.productName} - ${orderItem.presentationName}`,
        quantity: orderItem.quantity,
        unitPrice: orderItem.unitPrice,
        total: orderItem.total,
        sku: orderItem.productOrPresentationSku,
        aliquot: orderItem.cartItem.product.aliquot ?? params.defaultAliquot,
        type: 'product',
      });
      break;
    case 'multi-presentation':
      orderItem.presentations.items.forEach(item => {
        lineItems.push({
          categoryName: orderItem.cartItem.category.name,
          groupName: orderItem.cartItem.product.groupName,
          name: `${orderItem.productName} - ${item.name}`,
          quantity: item.quantity,
          unitPrice: item.unitPrice,
          total: item.price,
          sku: item.sku,
          aliquot: orderItem.cartItem.product.aliquot ?? params.defaultAliquot,
          type: 'presentation',
        });
      });
      break;
  }

  orderItem.options?.forEach(option => {
    option.items.forEach(item => {
      lineItems.push({
        categoryName: orderItem.cartItem.category.name,
        groupName: orderItem.cartItem.product.groupName,
        name: item.name,
        quantity: item.quantity,
        unitPrice: item.unitPrice,
        total: item.price,
        aliquot: item.aliquot ?? params.defaultAliquot,
        type: 'option-item',
      });
    });
  });

  return lineItems.filter(lineItem => lineItem.total > 0);
}

export function sortAsscending(a: string, b: string): number {
  if (a === b) {
    return 0;
  }
  if (a === 'undefined') {
    return 1;
  }
  if (b === 'undefined') {
    return -1;
  }
  return a < b ? -1 : 1;
}

export function getEndOrderToOrderTotalParams(endOrder: EndOrderDetails): OrderTotalParams {
  const deliveryCost = endOrder.deliveryCost;
  const outOfDeliveryZone = endOrder.outOfDeliveryZone;

  return {
    totalAmount: endOrder.totalAmount,
    totalProductDiscount: endOrder.totalProductDiscount,
    finalAmount: endOrder.finalAmount,
    couponDiscount: endOrder.couponDiscount,
    deliveryCost,
    outOfDeliveryZone,
    extraChargeAmount: endOrder.paymentMethodExtraCharge,
    discountAmount: endOrder.paymentMethodDiscount,
    foodBankDonationAmount: endOrder.foodBankDonationAmount,
  };
}

export function getSanitizedCustomFieldDetails(customFieldDetails?: CustomFieldDetail[]) {
  if (!customFieldDetails || customFieldDetails?.length === 0) {
    return undefined;
  }
  const sanitizedCustomFields = customFieldDetails
    .map(customFieldDetail => ({
      name: customFieldDetail.field.name,
      value: getCustomFieldDetailsValue(customFieldDetail),
    }))
    .filter(customField => Boolean(customField.value));

  return sanitizedCustomFields.length > 0 ? sanitizedCustomFields : undefined;
}

export function getCustomFieldDetailsValue(customFieldDetail: CustomFieldDetail): string {
  const parsedValue = parseCustomFieldValue(
    customFieldDetail.value,
    customFieldDetail.field.fieldType,
  );

  if (!parsedValue) {
    return null;
  }

  if (Array.isArray(parsedValue)) {
    return parsedValue.join(', ');
  } else if (customFieldDetail.field.fieldType === 'date') {
    if (parsedValue instanceof Date) {
      return format(parsedValue, 'dd/MM/yy');
    }
    if ((parsedValue as FirestoreTimestamp)?.toDate) {
      return format((parsedValue as FirestoreTimestamp)?.toDate(), 'dd/MM/yy');
    }
    return parsedValue.toString();
  } else {
    return parsedValue.toString();
  }
}

export function hasPaymentLink(paymentMethod: PaymentMethod) {
  if (paymentMethod?.hasLink) {
    return true;
  }
  return false;
}

export function getMapUrl(siteUrl: string, orderId: string) {
  return `${siteUrl}/pedidos/${orderId}/mapa`;
}

export function getPaymentUrl(siteUrl: string, orderId: string) {
  return `${siteUrl}/pedidos/${orderId}/pago`;
}

export function getTrackingUrl(siteUrl: string, orderId: string) {
  return `${siteUrl}/pedidos/${orderId}/envio`;
}

export function getCartItemsByCategory(
  cartItems: CartItem[],
): Record<string, Record<string, CartItem[]>> {
  // CategoryName -> GroupName -> Products[]
  const itemsByCategory: Record<string, Record<string, CartItem[]>> = {};

  // Groups items before processing
  cartItems.map(cartItem => {
    const categoryName = cartItem.category.name;
    const groupName = cartItem.product.groupName || 'undefined';

    if (!itemsByCategory[categoryName]) {
      itemsByCategory[categoryName] = {};
    }
    if (!itemsByCategory[categoryName][groupName]) {
      itemsByCategory[categoryName][groupName] = [];
    }
    itemsByCategory[categoryName][groupName].push(cartItem);
  });

  return itemsByCategory;
}

export const applyFormatToEveryLine = (
  message: string,
  formatLine: (line: string, index?: number) => string,
) => {
  const lines = message.split(/\n|\r/);

  return lines.map((line, index) => (line ? formatLine(line, index) : line)).join('\n');
};

export const hasAnyPriceModifier = (
  priceModifiers: Pick<
    OrderTotalParams,
    | 'totalProductDiscount'
    | 'deliveryCost'
    | 'couponDiscount'
    | 'extraChargeAmount'
    | 'discountAmount'
    | 'foodBankDonationAmount'
  >,
): boolean => {
  return [
    priceModifiers.totalProductDiscount,
    priceModifiers.deliveryCost,
    priceModifiers.couponDiscount,
    priceModifiers.discountAmount,
    priceModifiers.extraChargeAmount,
    // Food Bank Donation
    priceModifiers.foodBankDonationAmount,
  ].some(priceModifier => priceModifier > 0);
};
